import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/20/solid";
import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import Spinner from "../../common/Spinner";
import { Actions as PEOPLES_PENSION_ACTIONS } from "./store/peoplesPensionActions";
import { INTEGRATION_LOGO, INTEGRATION_NAME } from "./store/config";
import { toTitleCase } from "@/components/common/validation/Validators";

const SendDataToPeoplesPension = ({
	periodId,
	sendPayPeriodToPeoplesPension,
	getPayPeriodStatus,
	sending,
	status,
	connected,
	complete,
	toggleErrorsModal,
}) => {
	const icon = useMemo(() => {
		if (sending || status === "pending")
			return (
				<Spinner
					style={{ color: "green", width: 12, position: "relative", top: -8, right: -14 }}
					className="animate-spin -ml-1 mr-3"
				/>
			);
		if (status === "success")
			return (
				<CheckCircleIcon style={{ color: "green", width: 15, position: "relative", top: -8, right: -14 }} />
			);
		if (status === "error")
			return <XCircleIcon style={{ color: "red", width: 15, position: "relative", top: -8, right: -14 }} />;
	}, [connected, status, sending]);

	useEffect(() => {
		if (status !== "pending" || document.hidden) return; // if FPS status is not pending, do not check status
		const interval = setInterval(() => {
			if (status !== "pending") clearInterval(interval);
			getPayPeriodStatus(periodId);
		}, 15000); // Check Submission status every 15 seconds
		if (status !== "pending") clearInterval(interval); // if updated, clear interval

		return () => clearInterval(interval); // if component unmounted, clear interval
	}, [status]);

	const canSend = !sending && (!status || status === "error") && complete;

	const title = useMemo(() => {
		if (status) return toTitleCase(status);
		if (sending) return "Sending...";
		if (complete && (!status || status === "error")) return `Send to ${INTEGRATION_NAME}`;
		if (!complete) return "Payroll not complete";
	}, [status, complete]);

	if (!connected || !status) return null;
	return (
		<div
			title={title}
			type="button"
			complete={canSend}
			style={{
				background: "#ECEFF1",
				padding: 2,
				paddingBottom: 4,
				borderRadius: "50%",
				width: 24.5,
				height: 24.5,
				cursor: canSend ? "pointer" : "not-allowed",
				marginLeft: 10,
			}}
			onClick={() => {
				if (status === "error") {
					toggleErrorsModal(periodId);
					return;
				}
				if (canSend) sendPayPeriodToPeoplesPension(periodId);
			}}
		>
			<img
				src={INTEGRATION_LOGO}
				style={{ width: 19.5 }}
			/>
			{icon}
		</div>
	);
};

const mapStateToProps = (state, { periodId }) => {
	const { peoplesPension } = { ...state.integration?.pension };
	const currentPeriod = state.payPeriods?.periods?.find(({ id }) => id === periodId);
	const complete = (() => {
		switch (typeof currentPeriod?.complete) {
			case "boolean":
				return currentPeriod.complete;
			case "string":
				return !["no", "false"].includes(currentPeriod.complete);
			case "undefined":
				return !!currentPeriod.complete;
		}
	})();

	return {
		connected: peoplesPension?.connectionId,
		status: peoplesPension?.payPeriods?.find(({ payPeriodId }) => payPeriodId === periodId)?.status,
		sending: peoplesPension?.sendingPayroll?.includes(periodId),
		complete,
	};
};

const mapDispatchToProps = (dispatch) => ({
	sendPayPeriodToPeoplesPension: (periodId) =>
		dispatch(PEOPLES_PENSION_ACTIONS.sendPayPeriodToPeoplesPension(periodId)),
	getPayPeriodStatus: (periodId) => dispatch(PEOPLES_PENSION_ACTIONS.getPayPeriodStatus(periodId)),
	toggleErrorsModal: (periodId) => dispatch(PEOPLES_PENSION_ACTIONS.toggleErrorsModal(periodId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SendDataToPeoplesPension);
